import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
	AlertTriangle,
	ChefHat,
	CheckCircle2,
	Coins,
	Package,
	PackageX,
	Percent,
	Plus,
	Soup,
	TrendingUp,
	Trophy,
} from "lucide-react";
import { reportsAPI } from "@/lib/api";
import { useCurrency } from "@/hooks/useCurrency";
import { usePermission } from "@/hooks/usePermission";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ReportTable } from "../components/ReportTable";
import { RankedBars } from "../components/RankedBars";
import { StatTile } from "../components/StatTile";
import { Panel, ReportNote, ReportState, SplitRow, StatGrid, useReportQuery } from "../components/ReportBlocks";
import { formatPercent, num, share, type ReportColumn } from "../lib/columns";
import type { ReportBodyProps, ReportFiltersParams } from "../types";

const sumBy = <T,>(rows: T[], pick: (row: T) => number) => rows.reduce((s, r) => s + pick(r), 0);

type StockLevel = "out" | "low" | "ok";

/** Out = nothing on hand; low = flagged by the low-stock alert (the app sets status out_of_stock then). */
function stockLevel(p: any): StockLevel {
	const qty = num(p.quantity);
	if (qty <= 0) return "out";
	if (p.status === "out_of_stock" || (p.lowStockAlert != null && qty <= num(p.lowStockAlert))) return "low";
	return "ok";
}

const onHand = (p: any) => Math.max(0, num(p.quantity));

function StockBadge({ level }: { level: StockLevel }) {
	const { t } = useTranslation();
	const Icon = level === "ok" ? CheckCircle2 : AlertTriangle;
	return (
		<span
			className={cn(
				"inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium",
				level === "out"
					? "bg-destructive/10 text-destructive"
					: level === "low"
						? "bg-warning/10 text-warning"
						: "bg-success/10 text-success",
			)}
		>
			<Icon className="h-3 w-3" aria-hidden="true" />
			{t(`reports.stock.${level}`)}
		</span>
	);
}

/* ─────────────────────────────── Inventory valuation ─────────────────────────────── */

export function InventoryReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const query = useReportQuery(
		"inventory",
		{ branchId: params.branchId },
		() => reportsAPI.getInventoryReport(params.branchId) as Promise<{ summary?: any; products?: any[] }>,
	);
	return (
		<ReportState query={query}>
			{data => {
				// Only stock-tracked items have a meaningful quantity (menu items and services do not).
				const rows = (data?.products ?? []).filter(p => p.trackQuantity);
				const costValue = sumBy(rows, p => onHand(p) * num(p.purchasePrice));
				const retailValue = sumBy(rows, p => onHand(p) * num(p.sellingPrice));
				const levels = rows.map(stockLevel);
				const byCategory = new Map<string, number>();
				for (const p of rows) {
					const name = p.category?.name || t("reports.uncategorized");
					byCategory.set(name, (byCategory.get(name) ?? 0) + onHand(p) * num(p.purchasePrice));
				}
				const columns: ReportColumn<any>[] = [
					{ key: "product", label: t("reports.cols.product"), value: p => p.name },
					{
						key: "barcode",
						label: t("products.table.barcode"),
						value: p => p.barcode || "",
						hideOnMobile: true,
					},
					{
						key: "category",
						label: t("reports.cols.category"),
						value: p => p.category?.name || "",
						hideOnMobile: true,
					},
					{
						key: "branch",
						label: t("reports.cols.branch"),
						value: p => p.branch?.name || "",
						hideOnMobile: true,
					},
					{ key: "qty", label: t("reports.cols.onHand"), value: p => num(p.quantity), kind: "number" },
					{
						key: "cost",
						label: t("reports.cols.costPrice"),
						value: p => num(p.purchasePrice),
						kind: "money",
						currency,
						hideOnMobile: true,
					},
					{
						key: "costValue",
						label: t("reports.cols.stockValueCost"),
						value: p => onHand(p) * num(p.purchasePrice),
						kind: "money",
						currency,
						total: true,
					},
					{
						key: "retailValue",
						label: t("reports.cols.stockValueRetail"),
						value: p => onHand(p) * num(p.sellingPrice),
						kind: "money",
						currency,
						total: true,
						hideOnMobile: true,
					},
					{
						key: "level",
						label: t("reports.cols.stockStatus"),
						value: p => t(`reports.stock.${stockLevel(p)}`),
						render: p => <StockBadge level={stockLevel(p)} />,
						align: "center",
					},
				];
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={Coins}
								label={t("reports.kpi.stockValueCost")}
								value={costValue}
								format="money"
								currency={currency}
							/>
							<StatTile
								icon={TrendingUp}
								label={t("reports.kpi.stockValueRetail")}
								value={retailValue}
								format="money"
								currency={currency}
								hint={t("reports.kpi.potentialMargin", {
									value: formatPercent(share(retailValue - costValue, retailValue)),
								})}
							/>
							<StatTile
								icon={AlertTriangle}
								label={t("reports.kpi.lowStock")}
								value={levels.filter(l => l === "low").length}
								tone="warning"
								to={`/reports/low-stock${params.branchId ? `?branch=${params.branchId}` : ""}`}
							/>
							<StatTile
								icon={PackageX}
								label={t("reports.kpi.outOfStock")}
								value={levels.filter(l => l === "out").length}
								tone="negative"
							/>
						</StatGrid>
						<RankedBars
							title={t("reports.charts.stockValueByCategory")}
							currency={currency}
							showShare
							items={Array.from(byCategory.entries()).map(([name, value]) => ({
								key: name,
								label: name,
								value,
							}))}
						/>
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={p => p.id}
							rowHref={p => `/products/${p.id}`}
							exportName={exportName}
							emptyIcon={Package}
							defaultSort={{ key: "costValue", dir: "desc" }}
						/>
						<ReportNote>{t("reports.notes.inventory")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── Low stock ─────────────────────────────── */

/** The endpoint returns the 20 most urgent items. */
export const LOW_STOCK_LIMIT = 20;

export function LowStockReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const canPurchase = usePermission("purchases.create");
	const query = useReportQuery(
		"low-stock",
		{ branchId: params.branchId },
		() => reportsAPI.getLowStockProducts(params.branchId) as Promise<any[]>,
	);
	return (
		<ReportState query={query} tiles={2}>
			{data => {
				const rows = Array.isArray(data) ? data : [];
				const out = rows.filter(p => stockLevel(p) === "out").length;
				const columns: ReportColumn<any>[] = [
					{ key: "product", label: t("reports.cols.product"), value: p => p.name },
					{
						key: "barcode",
						label: t("products.table.barcode"),
						value: p => p.barcode || "",
						hideOnMobile: true,
					},
					{
						key: "category",
						label: t("reports.cols.category"),
						value: p => p.category?.name || "",
						hideOnMobile: true,
					},
					{ key: "qty", label: t("reports.cols.onHand"), value: p => num(p.quantity), kind: "number" },
					{
						key: "alert",
						label: t("reports.cols.alertLevel"),
						value: p => (p.lowStockAlert == null ? null : num(p.lowStockAlert)),
						kind: "number",
					},
					{
						key: "level",
						label: t("reports.cols.stockStatus"),
						value: p => t(`reports.stock.${stockLevel(p)}`),
						render: p => <StockBadge level={stockLevel(p)} />,
						align: "center",
					},
				];
				return (
					<div className="space-y-4">
						<StatGrid className="xl:grid-cols-3">
							<StatTile
								icon={AlertTriangle}
								label={t("reports.kpi.needReorder")}
								value={rows.length}
								tone="warning"
								hint={
									rows.length >= LOW_STOCK_LIMIT
										? t("reports.lowStock.limitHint", { count: LOW_STOCK_LIMIT })
										: undefined
								}
							/>
							<StatTile icon={PackageX} label={t("reports.kpi.outOfStock")} value={out} tone="negative" />
							{canPurchase && rows.length > 0 && (
								<div className="flex flex-col justify-center gap-2 rounded-2xl border border-dashed bg-card p-4 print:hidden">
									<p className="text-sm text-muted-foreground">{t("reports.lowStock.reorderHint")}</p>
									<Button asChild size="sm" className="self-start">
										<Link to="/purchases/new">
											<Plus className="h-4 w-4 me-2" />
											{t("reports.lowStock.createPurchase")}
										</Link>
									</Button>
								</div>
							)}
						</StatGrid>
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={p => p.id}
							rowHref={p => `/products/${p.id}`}
							exportName={exportName}
							emptyIcon={CheckCircle2}
							defaultSort={{ key: "qty", dir: "asc" }}
						/>
						<ReportNote>{t("reports.notes.lowStock")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── Ingredient consumption ─────────────────────────────── */

/** Kitchen endpoints take only branch and dates. */
const kitchenParams = (p: ReportFiltersParams) => ({
	branchId: p.branchId,
	startDate: p.startDate,
	endDate: p.endDate,
});

export function IngredientConsumptionReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const q = kitchenParams(params);
	const query = useReportQuery(
		"ingredient-consumption",
		q,
		() => reportsAPI.getIngredientConsumption(q) as Promise<{ items?: any[]; totalCost?: number }>,
	);
	return (
		<ReportState query={query} tiles={3}>
			{data => {
				const rows = data?.items ?? [];
				const total = num(data?.totalCost);
				const top = [...rows].sort((a, b) => num(b.cost) - num(a.cost))[0];
				const columns: ReportColumn<any>[] = [
					{ key: "product", label: t("reports.cols.ingredient"), value: r => r.productName },
					{
						key: "qty",
						label: t("reports.cols.quantityUsed"),
						value: r => num(r.quantityConsumed),
						kind: "number",
					},
					{ key: "unit", label: t("reports.cols.unit"), value: r => r.unit || "" },
					{
						key: "cost",
						label: t("reports.cols.cost"),
						value: r => num(r.cost),
						kind: "money",
						currency,
						total: true,
					},
					{
						key: "share",
						label: t("reports.cols.share"),
						value: r => share(num(r.cost), total),
						kind: "percent",
					},
				];
				return (
					<div className="space-y-4">
						<StatGrid className="xl:grid-cols-3">
							<StatTile
								icon={Coins}
								label={t("reports.kpi.ingredientCost")}
								value={total}
								format="money"
								currency={currency}
							/>
							<StatTile icon={Soup} label={t("reports.kpi.ingredientsUsed")} value={rows.length} />
							<StatTile
								icon={Trophy}
								label={t("reports.kpi.costliestIngredient")}
								value={top ? num(top.cost) : null}
								format="money"
								currency={currency}
								hint={top?.productName}
							/>
						</StatGrid>
						<RankedBars
							title={t("reports.charts.costByIngredient")}
							currency={currency}
							showShare
							items={rows.map(r => ({ key: r.productId, label: r.productName, value: num(r.cost) }))}
						/>
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={r => r.productId}
							rowHref={r => `/products/${r.productId}`}
							exportName={exportName}
							emptyIcon={Soup}
						/>
						<ReportNote>{t("reports.notes.recipes")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── Food cost % ─────────────────────────────── */

/** Common restaurant targets: up to 30% healthy, up to 35% worth watching, above that high. */
const FOOD_COST_GOOD = 30;
const FOOD_COST_WATCH = 35;

export function FoodCostReport({ params }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const q = kitchenParams(params);
	const query = useReportQuery(
		"food-cost",
		q,
		() =>
			reportsAPI.getFoodCost(q) as Promise<{
				totalRevenue: number;
				totalIngredientCost: number;
				foodCostPercent: number;
			}>,
	);
	return (
		<ReportState query={query} tiles={3}>
			{data => {
				const pct = num(data?.foodCostPercent);
				const revenue = num(data?.totalRevenue);
				const level =
					revenue <= 0 ? null : pct <= FOOD_COST_GOOD ? "good" : pct <= FOOD_COST_WATCH ? "watch" : "high";
				return (
					<div className="space-y-4">
						<StatGrid className="xl:grid-cols-3">
							<StatTile
								icon={TrendingUp}
								label={t("reports.kpi.sales")}
								value={revenue}
								format="money"
								currency={currency}
							/>
							<StatTile
								icon={Coins}
								label={t("reports.kpi.ingredientCost")}
								value={num(data?.totalIngredientCost)}
								format="money"
								currency={currency}
							/>
							<StatTile
								icon={Percent}
								label={t("reports.kpi.foodCostPercent")}
								value={revenue > 0 ? pct : null}
								format="percent"
								tone={
									level === "high"
										? "negative"
										: level === "watch"
											? "warning"
											: level === "good"
												? "positive"
												: "default"
								}
								hint={level ? t(`reports.foodCostLevel.${level}`) : undefined}
							/>
						</StatGrid>
						<Panel title={t("reports.foodCostLevel.title")}>
							{revenue <= 0 ? (
								<p className="py-8 text-center text-sm text-muted-foreground">
									{t("reports.empty.title")}
								</p>
							) : (
								<div className="space-y-3">
									<div className="relative h-3 rounded-full bg-destructive/10" aria-hidden="true">
										<div
											className="absolute inset-y-0 start-0 rounded-full bg-emerald-200"
											style={{ width: `${FOOD_COST_GOOD}%` }}
										/>
										<div
											className="absolute inset-y-0 bg-amber-200"
											style={{
												insetInlineStart: `${FOOD_COST_GOOD}%`,
												width: `${FOOD_COST_WATCH - FOOD_COST_GOOD}%`,
											}}
										/>
										<div
											className="absolute -top-1 h-5 w-1.5 rounded-full bg-primary"
											style={{ insetInlineStart: `calc(${Math.min(100, pct)}% - 3px)` }}
										/>
									</div>
									<div className="flex flex-wrap justify-between gap-2 text-xs text-muted-foreground">
										<span>
											{t("reports.foodCostLevel.good")} ≤ {FOOD_COST_GOOD}%
										</span>
										<span>
											{t("reports.foodCostLevel.watch")} ≤ {FOOD_COST_WATCH}%
										</span>
										<span>
											{t("reports.foodCostLevel.high")} &gt; {FOOD_COST_WATCH}%
										</span>
									</div>
									<p className="text-sm">
										{t("reports.foodCostLevel.explain", { value: formatPercent(pct) })}
									</p>
								</div>
							)}
						</Panel>
						<ReportNote>{t("reports.notes.foodCost")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── Recipe profitability ─────────────────────────────── */

export function RecipeProfitabilityReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const q = kitchenParams(params);
	const query = useReportQuery(
		"recipe-profitability",
		q,
		() => reportsAPI.getRecipeProfitability(q) as Promise<{ items?: any[] }>,
	);
	return (
		<ReportState query={query}>
			{data => {
				const rows = data?.items ?? [];
				const revenue = sumBy(rows, r => num(r.revenue));
				const cost = sumBy(rows, r => num(r.ingredientCost));
				const recipeHref = (r: any) => `/recipes/product/${r.menuProductId}`;
				const columns: ReportColumn<any>[] = [
					{ key: "item", label: t("reports.cols.menuItem"), value: r => r.menuProductName },
					{
						key: "revenue",
						label: t("reports.cols.revenue"),
						value: r => num(r.revenue),
						kind: "money",
						currency,
						total: true,
					},
					{
						key: "cost",
						label: t("reports.cols.ingredientCost"),
						value: r => num(r.ingredientCost),
						kind: "money",
						currency,
						total: true,
					},
					{
						key: "margin",
						label: t("reports.cols.margin"),
						value: r => num(r.margin),
						kind: "money",
						currency,
						total: true,
					},
					{
						key: "marginPct",
						label: t("reports.cols.marginPercent"),
						value: r => num(r.marginPercent),
						kind: "percent",
						render: r => (
							<span
								dir="ltr"
								className={cn(
									"tabular-nums",
									num(r.marginPercent) < 100 - FOOD_COST_WATCH && "font-semibold text-destructive",
								)}
							>
								{formatPercent(num(r.marginPercent))}
							</span>
						),
					},
				];
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={TrendingUp}
								label={t("reports.kpi.menuRevenue")}
								value={revenue}
								format="money"
								currency={currency}
							/>
							<StatTile
								icon={Coins}
								label={t("reports.kpi.ingredientCost")}
								value={cost}
								format="money"
								currency={currency}
							/>
							<StatTile
								icon={ChefHat}
								label={t("reports.kpi.grossMargin")}
								value={revenue - cost}
								format="money"
								currency={currency}
							/>
							<StatTile
								icon={Percent}
								label={t("reports.kpi.marginPercent")}
								value={revenue > 0 ? share(revenue - cost, revenue) : null}
								format="percent"
							/>
						</StatGrid>
						<SplitRow>
							<RankedBars
								title={t("reports.charts.topMargin")}
								currency={currency}
								items={rows.map(r => ({
									key: r.menuProductId,
									label: r.menuProductName,
									value: num(r.margin),
									href: recipeHref(r),
								}))}
							/>
							<RankedBars
								title={t("reports.charts.topRevenueItems")}
								currency={currency}
								items={rows.map(r => ({
									key: r.menuProductId,
									label: r.menuProductName,
									value: num(r.revenue),
									href: recipeHref(r),
								}))}
							/>
						</SplitRow>
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={r => r.menuProductId}
							rowHref={recipeHref}
							exportName={exportName}
							emptyIcon={ChefHat}
						/>
						<ReportNote>{t("reports.notes.recipes")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}
